import { Link } from 'react-router-dom';
import BrandWordmark from '../components/BrandWordmark';
import './Auth.css';

const POLICY_CONTENT = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'April 8, 2026',
    intro: 'This Privacy Policy explains how NiceBooster collects, uses, stores, and protects your information when you use our website and services.',
    sections: [
      {
        heading: 'Information We Collect',
        body: [
          'We collect the information you provide directly when creating an account, placing orders, making deposits, or contacting support. This may include your username, email address, payment details, order information, and support messages.',
          'We may also collect technical information such as device type, browser information, IP address, pages visited, and basic usage activity needed to operate and secure the platform.',
        ],
      },
      {
        heading: 'How We Use Your Information',
        body: [
          'We use your information to create and manage your account, process orders, handle payments, provide customer support, improve the platform, prevent fraud, and communicate important service updates.',
          'We do not sell your personal information to third parties.',
        ],
      },
      {
        heading: 'Payment and Transaction Data',
        body: [
          'Deposits and payment processing may be handled through third-party gateways such as mobile money or crypto payment providers. We store the details necessary to verify and reconcile your transactions, but sensitive payment credentials are processed by the payment provider.',
        ],
      },
      {
        heading: 'Data Sharing',
        body: [
          'We may share limited data with service providers, payment gateways, hosting providers, analytics tools, or authorities when required to operate the service, enforce our policies, or comply with legal obligations.',
        ],
      },
      {
        heading: 'Data Retention and Security',
        body: [
          'We keep account, order, and transaction records for as long as reasonably necessary for operations, legal compliance, dispute handling, and fraud prevention.',
          'We use reasonable administrative and technical measures to protect your information, but no online system can guarantee absolute security.',
        ],
      },
      {
        heading: 'Your Choices',
        body: [
          'You may update certain account information from your settings page. If you want your account reviewed for deletion or need help with your personal data, please contact support through the platform.',
        ],
      },
      {
        heading: 'Policy Updates',
        body: [
          'We may update this Privacy Policy from time to time. Continued use of the service after an update means you accept the revised policy.',
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'April 8, 2026',
    intro: 'These Terms of Service govern your access to and use of NiceBooster. By creating an account or using the platform, you agree to these terms.',
    sections: [
      {
        heading: 'Account Responsibilities',
        body: [
          'You are responsible for the accuracy of the information you provide and for maintaining the security of your account credentials.',
          'You must not use the platform for unlawful, abusive, fraudulent, or deceptive activity.',
        ],
      },
      {
        heading: 'Services and Orders',
        body: [
          'NiceBooster provides digital services that may depend on third-party suppliers and external platforms. Delivery times, refill behavior, completion speed, and final results can vary depending on the service selected.',
          'You are responsible for entering correct order details. Orders placed with incorrect links, usernames, or unsupported targets may fail or be non-refundable.',
        ],
      },
      {
        heading: 'Pricing and Availability',
        body: [
          'Service prices, descriptions, estimated delivery times, and availability may change at any time without prior notice.',
          'We reserve the right to cancel, limit, or refuse any order when required for platform stability, supplier issues, pricing errors, fraud prevention, or policy enforcement.',
        ],
      },
      {
        heading: 'Deposits and Balance',
        body: [
          'Deposits add funds to your NiceBooster balance for use on the platform. Exchange rates, gateway fees, and platform markups may affect the final credited value where applicable.',
          'Account balances have no cash value outside the platform unless expressly required by law.',
        ],
      },
      {
        heading: 'Prohibited Conduct',
        body: [
          'You may not attempt to abuse the service, manipulate pricing, reverse engineer restricted systems, interfere with other users, or use the platform in a way that harms NiceBooster, its suppliers, or third parties.',
        ],
      },
      {
        heading: 'Suspension and Termination',
        body: [
          'We may suspend or terminate accounts involved in abuse, fraud, payment disputes, chargebacks, policy violations, or security threats.',
        ],
      },
      {
        heading: 'Limitation of Liability',
        body: [
          'NiceBooster is provided on an as-available basis. To the maximum extent permitted by law, we are not liable for indirect, incidental, special, or consequential damages arising from use of the platform.',
        ],
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    updated: 'April 8, 2026',
    intro: 'This Refund Policy explains when refunds may or may not be granted for deposits, orders, and account balances on NiceBooster.',
    sections: [
      {
        heading: 'General Rule',
        body: [
          'Because NiceBooster provides digital services and account balance credits, refunds are not automatic and are reviewed based on the status of the order or transaction.',
        ],
      },
      {
        heading: 'Deposits',
        body: [
          'Successful deposits that have already been credited to your account balance are generally non-refundable.',
          'If a payment is charged by a gateway but not credited correctly due to a system issue, we will review the transaction and either credit the account properly or resolve it through support.',
        ],
      },
      {
        heading: 'Orders',
        body: [
          'Refunds may be issued when an order cannot be started, cannot be completed, is rejected by the supplier, or is manually reviewed and approved by support or an administrator.',
          'Refunds are usually returned to your NiceBooster account balance unless otherwise required by law.',
        ],
      },
      {
        heading: 'Non-Refundable Situations',
        body: [
          'Orders submitted with incorrect links, private accounts, invalid usernames, unsupported content, duplicated requests, or user-caused issues are generally not refundable.',
          'Normal fluctuations in social platform behavior, drops, delays, or third-party changes do not automatically qualify for a refund unless the service description specifically says otherwise.',
        ],
      },
      {
        heading: 'Chargebacks and Payment Disputes',
        body: [
          'Opening a payment dispute or chargeback without first contacting support may result in account suspension while the case is reviewed.',
        ],
      },
      {
        heading: 'How to Request Review',
        body: [
          'If you believe a refund is justified, contact support with your order ID, deposit reference, and a short explanation. Each case is reviewed based on service logs, gateway records, and supplier responses.',
        ],
      },
    ],
  },
};

export default function LegalPage({ type = 'privacy' }) {
  const policy = POLICY_CONTENT[type] || POLICY_CONTENT.privacy;

  return (
    <div className="auth-page">
      <div className="auth-bg-grid" />
      <div className="auth-card auth-legal-card">
        <div className="auth-logo">
          <BrandWordmark className="auth-brand-mark" />
        </div>

        <div className="auth-legal-top">
          <h1 className="auth-title">{policy.title}</h1>
          <p className="auth-sub">Last updated: {policy.updated}</p>
        </div>

        <div className="auth-legal-content">
          <p>{policy.intro}</p>

          {policy.sections.map((section) => (
            <section key={section.heading} className="auth-legal-section">
              <h2>{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="auth-legal-actions">
          <Link to="/register" className="btn btn-primary btn-lg" style={{ textDecoration: 'none', justifyContent: 'center' }}>
            Back to Register
          </Link>
          <Link to="/login" className="btn btn-secondary btn-lg" style={{ textDecoration: 'none', justifyContent: 'center' }}>
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
